import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  RotateCcw,
  Stethoscope,
  Bot,
  Clock,
  Flame,
  Bell,
  ShieldCheck,
  ArrowUpRight,
  ChevronRight,
  Users,
  FileText,
} from "lucide-react";

const DEFAULT_THRESHOLDS = {
  alta_no_completada_h: 24,
  firma_pendiente_h: 48,
  psicotecnico_pendiente_h: 72,
  documentacion_solicitada_h: 24,
  escalation_alta_multiplier: 2,
  escalation_critica_multiplier: 3,
  max_auto_reminders: 2,
  admin_alert_min_overdue: 5,
  admin_alert_min_hours_between: 24,
};

const FIELDS = [
  {
    section: "Umbrales de detección",
    icon: Clock,
    items: [
      { key: "alta_no_completada_h",       label: "Alta digital sin completar",   suffix: "h", help: "Tiempo desde el envío del enlace hasta considerarlo pendiente." },
      { key: "firma_pendiente_h",          label: "Firma pendiente",              suffix: "h", help: "Documento enviado a firma sin firmar." },
      { key: "psicotecnico_pendiente_h",   label: "Psicotécnico pendiente",       suffix: "h", help: "Alta completada pero psicotécnico aún no recibido." },
      { key: "documentacion_solicitada_h", label: "Documentación solicitada",     suffix: "h", help: "Documento marcado como \"Solicitado\" sin respuesta." },
    ],
  },
  {
    section: "Escalado de prioridad",
    icon: Flame,
    items: [
      { key: "escalation_alta_multiplier",    label: "Multiplicador → ALTA",    suffix: "×", help: "El umbral base × este valor pasa la incidencia a ALTA." },
      { key: "escalation_critica_multiplier", label: "Multiplicador → CRÍTICA", suffix: "×", help: "El umbral base × este valor pasa la incidencia a CRÍTICA (requiere revisión humana)." },
    ],
  },
  {
    section: "Límites y avisos al admin",
    icon: Bell,
    items: [
      { key: "max_auto_reminders",             label: "Máx. recordatorios automáticos", suffix: "", help: "Después de este número KAI escala la incidencia y no envía más avisos al alumno." },
      { key: "admin_alert_min_overdue",        label: "Umbral email al admin",          suffix: "", help: "Se envía email al admin cuando el nº de incidencias pendientes supera este valor." },
      { key: "admin_alert_min_hours_between",  label: "Frecuencia mínima entre emails", suffix: "h", help: "Rate-limit para evitar spam al admin." },
    ],
  },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
}

function ThresholdRow({ item, value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-center py-2">
      <div className="sm:col-span-2">
        <div className="text-sm font-semibold text-neutral-900">{item.label}</div>
        <div className="text-[11px] text-neutral-500 leading-snug">{item.help}</div>
      </div>
      <div className="sm:col-span-2 flex items-center gap-2">
        <input
          type="number"
          min={1}
          max={9999}
          value={value}
          onChange={(e) => onChange(item.key, parseInt(e.target.value, 10) || 1)}
          disabled={disabled}
          className="w-24 h-10 rounded-lg border border-neutral-300 bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-neutral-100"
          data-testid={`kai-threshold-input-${item.key}`}
        />
        {item.suffix && <span className="text-xs font-semibold text-neutral-500">{item.suffix}</span>}
      </div>
      <div className="text-right text-[10px] font-mono text-neutral-400">
        default: {DEFAULT_THRESHOLDS[item.key]}
      </div>
    </div>
  );
}

/** Doctor result panel — rendered under the Doctor button after a scan. */
function DoctorResults({ result, onGotoTray, onClose }) {
  if (!result) return null;
  const lineIcon = (n) => (n === 0 ? "✅" : "⚠");
  const criticalIcon = result.critical_open === 0 ? "✅" : "🔥";
  return (
    <div
      className="rounded-2xl border border-blue-200 bg-blue-50 p-4 mt-3 space-y-3"
      data-testid="kai-doctor-result"
    >
      <div className="flex items-center gap-2">
        <Stethoscope className="h-4 w-4 text-blue-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-blue-800">
          Último análisis KAI · {fmtDate(result.at)}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm font-mono text-neutral-800">
        <div>✅ <b>{result.scanned_students}</b> expedientes revisados ({result.active_students} activos)</div>
        <div>{criticalIcon} <b>{result.critical_open}</b> incidencias críticas abiertas</div>
        <div>{lineIcon(result.alta_pending)} <b>{result.alta_pending}</b> altas digitales pendientes</div>
        <div>{lineIcon(result.psico_pending)} <b>{result.psico_pending}</b> psicotécnicos pendientes</div>
        <div>{lineIcon(result.doc_pending)} <b>{result.doc_pending}</b> documentos sin validar</div>
        <div>{lineIcon(result.firma_pending)} <b>{result.firma_pending}</b> firmas pendientes</div>
        <div>✅ <b>{result.auto_close_candidates}</b> incidencias auto-cerrables</div>
        <div>👥 <b>{result.requires_human_open}</b> requieren decisión humana</div>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={onGotoTray}
          data-testid="kai-doctor-goto-tray"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
        >
          <ArrowUpRight className="h-3.5 w-3.5" /> Ver bandeja KAI
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-xs font-semibold"
        >
          Cerrar
        </button>
      </div>

      <div className="text-[11px] text-neutral-500 italic flex items-start gap-1 pt-1 border-t border-blue-200">
        <ShieldCheck className="h-3 w-3 text-emerald-600 mt-0.5 shrink-0" />
        <span>
          Diagnóstico <b>solo lectura</b>. No se ha modificado ningún estado, no se ha enviado ningún mensaje ni se han creado recordatorios.
        </span>
      </div>
    </div>
  );
}

export default function KaiSettings({ onNavigateToTray }) {
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [pristine, setPristine] = useState(DEFAULT_THRESHOLDS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [doctor, setDoctor] = useState(null);
  const [justReset, setJustReset] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kai/thresholds");
      const merged = { ...DEFAULT_THRESHOLDS, ...(data || {}) };
      setThresholds(merged);
      setPristine(merged);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = Object.keys(pristine).some((k) => (pristine[k] ?? 0) !== (thresholds[k] ?? 0));

  const updateField = (key, value) => {
    setThresholds((t) => ({ ...t, [key]: Math.max(1, Math.min(9999, Number(value) || 1)) }));
  };

  const save = async () => {
    // Only send the diff so the server-side sudo prompt happens only if
    // there's an actual change to commit.
    const diff = {};
    for (const k of Object.keys(thresholds)) {
      if ((pristine[k] ?? 0) !== (thresholds[k] ?? 0)) diff[k] = thresholds[k];
    }
    if (Object.keys(diff).length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.put("/kai/thresholds", diff);
      const merged = { ...DEFAULT_THRESHOLDS, ...(data || {}) };
      setThresholds(merged);
      setPristine(merged);
      setJustReset(false);
      toast.success("Umbrales KAI guardados");
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 440 && status !== 428) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setThresholds(DEFAULT_THRESHOLDS);
    setJustReset(true);
  };

  const updateFieldOnly = (key, value) => {
    if (justReset) setJustReset(false);
    updateField(key, value);
  };

  const runDoctor = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/kai/doctor/scan", {});
      setDoctor(data);
      toast.success("Diagnóstico completado");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };

  return (
    <section
      className="rounded-3xl border border-neutral-200 bg-white p-5 sm:p-7 space-y-5"
      data-testid="kai-settings-panel"
    >
      {/* Header */}
      <div className="flex items-center flex-wrap gap-3">
        <div className="h-10 w-10 rounded-xl bg-orange-500 text-white flex items-center justify-center shrink-0">
          <Bot className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold leading-tight">KAI · Supervisor</h2>
          <p className="text-xs text-neutral-500">Umbrales de detección, escalado y avisos. Diagnóstico en tiempo real.</p>
        </div>
        <button
          type="button"
          onClick={runDoctor}
          disabled={scanning}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold shadow-sm disabled:opacity-50"
          data-testid="kai-doctor-run"
          title="Análisis inmediato (solo lectura, no modifica nada)"
        >
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Stethoscope className="h-4 w-4" />}
          KAI Doctor
        </button>
      </div>

      <DoctorResults result={doctor} onGotoTray={onNavigateToTray} onClose={() => setDoctor(null)} />

      {loading ? (
        <div className="py-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-orange-500 mx-auto" />
        </div>
      ) : (
        <>
          {FIELDS.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.section} className="space-y-1">
                <div className="flex items-center gap-2 pt-2">
                  <Icon className="h-4 w-4 text-orange-500" />
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
                    {sec.section}
                  </h3>
                </div>
                <div className="divide-y divide-neutral-100">
                  {sec.items.map((item) => (
                    <ThresholdRow
                      key={item.key}
                      item={item}
                      value={thresholds[item.key] ?? DEFAULT_THRESHOLDS[item.key]}
                      onChange={updateFieldOnly}
                      disabled={saving}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Save bar */}
          <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-neutral-100">
            {justReset && !dirty && (
              <span
                className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 text-[11px] font-bold px-2 py-0.5"
                data-testid="kai-thresholds-reset-hint"
              >
                Valores por defecto restaurados
              </span>
            )}
            {justReset && dirty && (
              <span
                className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 text-[11px] font-bold px-2 py-0.5"
                data-testid="kai-thresholds-reset-hint"
              >
                Volviendo a defaults del sistema
              </span>
            )}
            {dirty && !justReset && (
              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold px-2 py-0.5">
                Cambios sin guardar
              </span>
            )}
            <button
              type="button"
              onClick={resetDefaults}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-sm font-semibold disabled:opacity-50"
              data-testid="kai-thresholds-reset"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Valores por defecto
            </button>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-sm disabled:opacity-40"
                data-testid="kai-thresholds-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Guardar cambios
              </button>
            </div>
          </div>

          <div className="text-[11px] text-neutral-500 italic border-t border-neutral-100 pt-3">
            <b>Regla inviolable</b>: KAI siempre respeta <code>No contactar</code>, <code>Baja voluntaria</code>,
            <code>Pendiente decisión</code>, <code>Automatizaciones pausadas</code> y el máximo de recordatorios
            configurado. La criticidad de una incidencia afecta al equipo, nunca al cliente.
          </div>
        </>
      )}
    </section>
  );
}
